import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FloatingPortal, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { ReactRenderer } from '@tiptap/react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useRegisterWithKeyMaster } from '../../contexts/KeyMaster';
import { GlassWrapper } from '../shared/GlassWrapper';

// Shared wrapper for TipTap suggestion dropdowns
// Handles Floating UI positioning, KeyMaster registration, and keyboard navigation
const HOTKEY_OPTIONS = { enableOnFormTags: true, enableOnContentEditable: true, preventDefault: true };

function SuggestionPortal({ clientRect, onClose, keychainId, children }) {
  const listRef = useRef(null);

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open: true,
    onOpenChange: (open) => {
      if (!open) onClose?.();
    },
    placement: 'top-start',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const [shouldDisplay, setShouldDisplay] = useState(false);

  // This is a dumb hack. Floating UI briefly positions incorrectly on mount, triggering hover states that stick around after it snaps to the correct position. A tiny delay prevents this.
  useEffect(() => {
    if (isPositioned) {
      const timer = setTimeout(() => {
        setShouldDisplay(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShouldDisplay(false);
    }
  }, [isPositioned]);

  // escapeKey: false - escape is handled via KeyMaster
  const dismiss = useDismiss(context, { escapeKey: false });
  const { getFloatingProps } = useInteractions([dismiss]);

  const { unlocked } = useRegisterWithKeyMaster(keychainId);

  const handleEscape = useCallback(() => {
    if (unlocked()) {
      onClose?.();
    }
  }, [unlocked, onClose]);

  const handleArrowUp = useCallback(() => {
    if (unlocked()) {
      listRef.current?.onKeyDown({ event: { key: 'ArrowUp' } });
    }
  }, [unlocked]);

  const handleArrowDown = useCallback(() => {
    if (unlocked()) {
      listRef.current?.onKeyDown({ event: { key: 'ArrowDown' } });
    }
  }, [unlocked]);

  const handleEnter = useCallback(() => {
    if (unlocked()) {
      listRef.current?.onKeyDown({ event: { key: 'Enter' } });
    }
  }, [unlocked]);

  const handleTab = useCallback(() => {
    if (unlocked()) {
      listRef.current?.onKeyDown({ event: { key: 'Tab' } });
    }
  }, [unlocked]);

  useHotkeys('escape', handleEscape, HOTKEY_OPTIONS, [handleEscape]);
  useHotkeys('up', handleArrowUp, HOTKEY_OPTIONS, [handleArrowUp]);
  useHotkeys('down', handleArrowDown, HOTKEY_OPTIONS, [handleArrowDown]);
  useHotkeys('enter', handleEnter, HOTKEY_OPTIONS, [handleEnter]);
  useHotkeys('tab', handleTab, HOTKEY_OPTIONS, [handleTab]);

  // Update the virtual reference element when clientRect changes
  useLayoutEffect(() => {
    if (clientRect) {
      refs.setReference({
        getBoundingClientRect: clientRect,
      });
    }
  }, [clientRect, refs]);

  return (
    <FloatingPortal>
      <GlassWrapper
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          // Hide until positioned to prevent hover flash at initial position
          display: shouldDisplay ? 'block' : 'none',
        }}
        {...getFloatingProps()}
        className="z-(--stack-modal-dropdown) gap-0"
      >
        {React.cloneElement(children, { ref: listRef })}
      </GlassWrapper>
    </FloatingPortal>
  );
}

// Helper to create the TipTap render lifecycle for suggestions
// PortalComponent should be a component that wraps SuggestionPortal with the appropriate list
export function createSuggestionRender(PortalComponent) {
  return () => {
    let component = null;

    const cleanup = () => {
      if (component) {
        component.destroy();
        component = null;
      }
    };

    return {
      onStart: (props) => {
        if (props.items.length === 0) {
          return;
        }

        component = new ReactRenderer(PortalComponent, {
          props: {
            ...props,
            clientRect: props.clientRect,
            query: props.query,
            onClose: cleanup,
          },
          editor: props.editor,
        });
      },

      onUpdate(props) {
        if (props.items.length === 0) {
          cleanup();
          return;
        }

        // If component doesn't exist yet but we have items, create it
        if (!component && props.items.length > 0) {
          this.onStart(props);
          return;
        }

        // Update the component with new props
        if (component) {
          component.updateProps({
            ...props,
            clientRect: props.clientRect,
            query: props.query,
            onClose: cleanup,
          });
        }
      },

      onKeyDown(props) {
        if (!component) return false;

        // Keyboard handling is done via useHotkeys in the React component
        // Return true for keys we handle to prevent TipTap default behavior
        const handledKeys = ['Escape', 'ArrowUp', 'ArrowDown', 'Enter', 'Tab'];
        return handledKeys.includes(props.event.key);
      },

      onExit() {
        cleanup();
      },
    };
  };
}

export default SuggestionPortal;
